import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DailyContent } from '@tarot/contracts';
import { http } from '@/api/client';
import { keys, useDaily, useInvalidate, useInventory, useMe } from '@/api/hooks';
import { useUi } from '@/app/store';
import { TarotCard } from '@/components/TarotCard';
import { Skeleton, fmtTime, plural } from '@/components/ui';
import { platform } from '@/platform';
import { Icon, type IconName } from '@/components/Icon';

const SPHERES: { k: keyof DailyContent; cls: string; ic: IconName; t: string }[] = [
  { k: 'work', cls: 'work', ic: 'work', t: 'Работа' },
  { k: 'love', cls: 'love', ic: 'love', t: 'Любовь' },
  { k: 'health', cls: 'health', ic: 'health', t: 'Здоровье' },
  { k: 'advice', cls: 'advice', ic: 'advice', t: 'Совет' },
];

export function DailyScreen() {
  const nav = useNavigate();
  const { data: daily, isError, error } = useDaily();
  const { data: inv } = useInventory();
  const { data: me } = useMe();
  const invalidate = useInvalidate();
  const toast = useUi((s) => s.showToast);
  const [opening, setOpening] = useState(false);
  const [local, setLocal] = useState<{ content: DailyContent; streak: number; bonus: 'ticket' | null } | null>(null);

  const content = local?.content ?? daily?.content ?? null;
  const flipped = !!content;
  const deck = inv?.decks.find((d) => d.id === daily?.deckId);

  const open = async () => {
    if (!daily || flipped || opening) return;
    setOpening(true);
    platform.haptic('medium');
    try {
      const r = await http.post<{ content: DailyContent; streak: number; bonus: 'ticket' | null }>('/daily-card/open');
      setTimeout(() => setLocal(r), 500);
      void invalidate(keys.me, keys.daily);
    } catch (e) { toast((e as Error).message, true); } finally { setOpening(false); }
  };

  return (
    <div className="screen no-nav">
      <div className="topbar"><button className="back" onClick={() => nav(-1)}><Icon name="back" size={18} />Назад</button></div>
      <div className="center hero"><div className="eyebrow"><Icon name="sun" size={14} />{daily ? new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) : ''}</div><h1>Карта дня</h1><p className="muted">Ваше послание на сегодня</p></div>
      <div className="card lg stack">
        {isError && <div className="empty">{(error as Error).message}</div>}
        {!daily && !isError && <Skeleton h={260} />}
        {daily && (
          <>
            <TarotCard card={daily.card} back={deck?.backStyle} reversed={!daily.card.upright} flipped={flipped} className={`lg${flipped ? '' : ' glow'}`} onClick={open} />
            {!flipped && <p className="dim center" style={{ marginTop: 6 }}>Нажмите, чтобы открыть</p>}
            {content && (
              <div className="stack">
                <div className="center">
                  <h2>{daily.card.name}</h2>
                  <div className="dim">Старшие арканы · {daily.card.upright ? 'прямое' : 'перевёрнутое'} положение</div>
                  <p className="muted" style={{ fontStyle: 'italic', margin: '6px 0' }}>«{content.keywords}»</p>
                </div>
                <div className="spheres">
                  {SPHERES.map((s, i) => (
                    <div key={s.k} className={`sphere ${s.cls}`} style={{ animation: `fadein .4s ${i * 0.1}s both` }}>
                      <div className="ic"><Icon name={s.ic} size={18} /></div>
                      <div><div className="t">{s.t}</div><div>{content[s.k]}</div></div>
                    </div>
                  ))}
                </div>
                <div className="card detail"><h3><Icon name="sparkle" size={16} filled />Послание на сегодня</h3><p>{content.detail}</p></div>
                {(local?.streak ?? me?.streak.current ?? 0) > 0 && (
                  <div className="center streak"><Icon name="flame" size={14} filled />Стрик: {local?.streak ?? me?.streak.current} {plural(local?.streak ?? me?.streak.current ?? 0, 'день', 'дня', 'дней')}{local?.bonus === 'ticket' && ' · +1 билет на крутку!'}</div>
                )}
                <div className="row">
                  <button className="btn ghost" onClick={() => platform.share(`Моя карта дня — ${daily.card.name}: ${content.keywords}. ${daily.card.quote}`, location.origin)}><Icon name="share" size={18} />Поделиться</button>
                  <button className="btn primary" onClick={() => nav('/', { replace: true })}><Icon name="sparkle" size={16} filled />Расклад</button>
                </div>
                <p className="dim center">Следующая карта через {fmtTime(daily.resetInSeconds)}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
